import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	Input,
	Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import JobsListPagination from "../JobsListPagination";
import { getListRangeLabel } from "../aiVisibilityListConstants";
import SchemaOptimizerIconButton from "./SchemaOptimizerIconButton";
import SchemaOptimizerStatusBadge from "./SchemaOptimizerStatusBadge";
import {
	getSchemaOptimizerLatestJob,
	isSchemaOptimizerJobInFlight,
	listSchemaOptimizerJobs,
	regenerateSchemaOptimizerJob,
} from "../../../services/schemaOptimizerApi";
import {
	isGeneratedContentSource,
	jobSourceLabel,
	jobTimestamp,
} from "./schemaOptimizerUtils";

export default function SchemaOptimizerJobsCard({
	websiteId,
	refreshKey = 0,
	onOpenGenerate,
}) {
	const navigate = useNavigate();
	const [jobs, setJobs] = useState([]);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(0);
	const [pageSize] = useState(10);
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [loading, setLoading] = useState(true);
	const [regeneratingUrl, setRegeneratingUrl] = useState(null);
	const [selectedUrls, setSelectedUrls] = useState(() => new Set());
	const [bulkRegenerating, setBulkRegenerating] = useState(false);
	const searchTimer = useRef(null);

	useEffect(() => {
		if (searchTimer.current) clearTimeout(searchTimer.current);
		searchTimer.current = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(0);
		}, 300);
		return () => {
			if (searchTimer.current) clearTimeout(searchTimer.current);
		};
	}, [searchInput]);

	const load = async () => {
		if (!websiteId) {
			setJobs([]);
			setLoading(false);
			return;
		}
		setLoading(true);
		const res = await listSchemaOptimizerJobs(websiteId, {
			skip: page * pageSize,
			limit: pageSize,
			q: search,
		});
		setLoading(false);
		if (!res.ok) {
			toast.error(res.message || "Could not load jobs");
			setJobs([]);
			setTotal(0);
			setHasMore(false);
			return;
		}
		setJobs(res.jobs);
		setTotal(res.total);
		setHasMore(res.hasMore);
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [websiteId, page, pageSize, search, refreshKey]);

	useEffect(() => {
		setSelectedUrls(new Set());
	}, [page, websiteId, search]);

	useEffect(() => {
		if (!websiteId || loading) return undefined;
		const active = jobs.some((job) => isSchemaOptimizerJobInFlight(job.status));
		if (!active) return undefined;
		const timer = setInterval(load, 3000);
		return () => clearInterval(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jobs, loading, websiteId]);

	async function openLatest(url) {
		const res = await getSchemaOptimizerLatestJob(websiteId, url);
		if (!res.ok || !res.job?.id) {
			toast.error(res.message || "Could not open job");
			return;
		}
		const id = res.job.id ?? res.job.job_id;
		navigate(`/aivisibility/schema-optimizer/view?jobId=${encodeURIComponent(id)}`);
	}

	async function handleRegenerate(job) {
		if (isGeneratedContentSource(job)) {
			toast.info("Regenerate from the session page for content-based jobs.");
			return;
		}
		setRegeneratingUrl(job.url);
		try {
			const latest = await getSchemaOptimizerLatestJob(websiteId, job.url);
			const jobId = latest.job?.id ?? latest.job?.job_id;
			if (!latest.ok || jobId == null) {
				toast.error(latest.message || "Could not resolve job");
				return;
			}
			const res = await regenerateSchemaOptimizerJob(websiteId, jobId);
			if (!res.ok) {
				toast.error(res.message || "Could not regenerate");
				return;
			}
			navigate(
				`/aivisibility/schema-optimizer/view?jobId=${encodeURIComponent(res.job.jobId)}`
			);
		} finally {
			setRegeneratingUrl(null);
		}
	}

	const selectableOnPage = jobs.filter((job) => !isGeneratedContentSource(job));
	const allSelected =
		selectableOnPage.length > 0 &&
		selectableOnPage.every((job) => selectedUrls.has(job.url));

	function toggleSelectAll() {
		if (allSelected) {
			setSelectedUrls(new Set());
			return;
		}
		setSelectedUrls(new Set(selectableOnPage.map((job) => job.url)));
	}

	function toggleRow(url) {
		setSelectedUrls((prev) => {
			const next = new Set(prev);
			if (next.has(url)) next.delete(url);
			else next.add(url);
			return next;
		});
	}

	async function handleBulkRegenerate() {
		const urls = [...selectedUrls];
		if (!urls.length || bulkRegenerating) return;
		setBulkRegenerating(true);
		let ok = 0;
		try {
			for (const url of urls) {
				const latest = await getSchemaOptimizerLatestJob(websiteId, url);
				const jobId = latest.job?.id ?? latest.job?.job_id;
				if (!latest.ok || jobId == null) continue;
				const res = await regenerateSchemaOptimizerJob(websiteId, jobId);
				if (res.ok) ok += 1;
			}
			if (ok === 0) {
				toast.error("Could not regenerate selected jobs");
			} else {
				toast.success(`Started ${ok} regeneration${ok !== 1 ? "s" : ""}`);
				setSelectedUrls(new Set());
				load();
			}
		} finally {
			setBulkRegenerating(false);
		}
	}

	return (
		<Card className="schema-optimizer__jobs-card shadow-sm border mt-4">
				<CardHeader className="bg-white">
					<div className="schema-optimizer__jobs-header">
						<div>
							<h5 className="mb-0">Your jobs</h5>
							<p className="text-muted small mb-0 mt-1">
								{loading
									? "Loading…"
									: `${total} unique URL${total !== 1 ? "s" : ""} · ${getListRangeLabel(
											page,
											pageSize,
											total
										)}`}
							</p>
						</div>
						<div className="schema-optimizer__jobs-toolbar">
							<Input
								type="search"
								placeholder="Search URLs"
								className="schema-optimizer__search"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
							/>
							<Button
								color="primary"
								outline
								size="sm"
								className="schema-optimizer__jobs-regenerate-btn"
								disabled={selectedUrls.size === 0 || bulkRegenerating}
								title={
									selectedUrls.size > 0
										? `Regenerate ${selectedUrls.size} selected`
										: "Select URLs to regenerate"
								}
								onClick={handleBulkRegenerate}
							>
								{bulkRegenerating ? (
									<Spinner size="sm" />
								) : (
									<>
										<i className="fas fa-redo me-2" aria-hidden />
										Regenerate
										{selectedUrls.size > 0 ? ` (${selectedUrls.size})` : ""}
									</>
								)}
							</Button>
						</div>
					</div>
				</CardHeader>
				<CardBody>
					{loading && jobs.length === 0 ? (
						<div className="text-center py-4">
							<Spinner size="sm" />
						</div>
					) : jobs.length === 0 ? (
						<div className="text-center py-4">
							<p className="text-muted mb-3">No schema jobs yet.</p>
							{onOpenGenerate && (
								<Button color="primary" size="sm" onClick={onOpenGenerate}>
									Generate schema
								</Button>
							)}
						</div>
					) : (
						<div className="table-responsive">
							<table className="table table-sm align-middle mb-0 schema-optimizer__jobs-table">
								<thead>
									<tr className="text-muted small">
										<th className="schema-optimizer__jobs-check-col">
											<input
												type="checkbox"
												className="form-check-input"
												checked={allSelected}
												disabled={selectableOnPage.length === 0}
												onChange={toggleSelectAll}
												aria-label="Select all URLs on this page"
											/>
										</th>
										<th>URL</th>
										<th className="schema-optimizer__jobs-col-center">Updated</th>
										<th className="schema-optimizer__jobs-col-center">Status</th>
										<th className="schema-optimizer__jobs-col-center">Actions</th>
									</tr>
								</thead>
								<tbody>
									{jobs.map((job) => {
										const ts = jobTimestamp(job);
										const canSelect = !isGeneratedContentSource(job);
										return (
											<tr key={job.url}>
												<td className="schema-optimizer__jobs-check-col">
													{canSelect ? (
														<input
															type="checkbox"
															className="form-check-input"
															checked={selectedUrls.has(job.url)}
															onChange={() => toggleRow(job.url)}
															aria-label={`Select ${job.url}`}
														/>
													) : null}
												</td>
												<td className="small">
													{isGeneratedContentSource(job) ? (
														jobSourceLabel(job)
													) : (
														<a href={job.url} target="_blank" rel="noopener noreferrer">
															{job.url}
														</a>
													)}
												</td>
												<td className="schema-optimizer__jobs-col-center text-muted small">
													{ts ? ts.toLocaleDateString() : "—"}
												</td>
												<td className="schema-optimizer__jobs-col-center">
													<SchemaOptimizerStatusBadge status={job.status} />
												</td>
												<td className="schema-optimizer__jobs-col-center">
													<div className="schema-optimizer__jobs-actions">
														<SchemaOptimizerIconButton
															title="View"
															iconClass="far fa-eye"
															onClick={() => openLatest(job.url)}
														/>
														<SchemaOptimizerIconButton
															title="Regenerate"
															iconClass="fas fa-redo"
															disabled={
																regeneratingUrl === job.url ||
																isSchemaOptimizerJobInFlight(job.status) ||
																isGeneratedContentSource(job)
															}
															onClick={() => handleRegenerate(job)}
														/>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
					<JobsListPagination
						page={page}
						pageSize={pageSize}
						total={total}
						hasMore={hasMore}
						onPageChange={setPage}
						loading={loading}
					/>
				</CardBody>
		</Card>
	);
}
